import { useState } from "react";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import { Ad } from "@/models/ad";
import { toast } from "sonner";
import { updateAdMaterialUrl } from "@/contexts/data/supabaseAdsCrud";

export const useAdManagement = () => {
  const { 
    ads, 
    getScreenById, 
    addAd, 
    updateAd, 
    deleteAd, 
    refreshData, 
    isLoading, 
    searchAds 
  } = useData();
  
  const { user, isAdmin } = useAuth();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Estados para la búsqueda
  const [searchName, setSearchName] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const currentUserName = user?.profile?.full_name || user?.email || 'Usuario';

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      await searchAds(searchName || undefined, searchDate || undefined);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = async () => {
    setSearchName('');
    setSearchDate('');
    await refreshData();
  };

  const openAddDialog = () => {
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (ad: Ad) => {
    // Verificar que el usuario pueda editar este anuncio
    if (!canEditOrDeleteAd(ad.sellerId)) {
      toast.error("No tienes permiso para editar este anuncio");
      return;
    }
    
    setSelectedAd(ad);
    setIsMaterialDialogOpen(true); // Cambiar a material dialog en lugar del edit dialog completo
  };

  const openMaterialDialog = (ad: Ad) => {
    // Verificar que el usuario pueda editar este anuncio
    if (!canEditOrDeleteAd(ad.sellerId)) {
      toast.error("No tienes permiso para editar este anuncio");
      return;
    }
    
    setSelectedAd(ad);
    setIsMaterialDialogOpen(true);
  };

  const openDeleteDialog = (ad: Ad) => {
    setSelectedAd(ad);
    setIsDeleteDialogOpen(true);
  };

  const handleAddAd = (data: any) => {
    console.log("Adding ad with screens:", data.screenIds);
    console.log("Material URL submitted:", data.materialUrl);
    
    const selectedScreensInfo = data.screenIds.map((screenId: string) => {
      const screen = getScreenById(screenId);
      return {
        screenId,
        screenName: screen?.name || ''
      };
    });

    if (user) {
      addAd({
        client_name: data.adName,
        screens_json: selectedScreensInfo,
        screen_id: data.screenIds[0],
        start_date: data.startDate,
        end_date: data.endDate,
        amount: data.amount,
        seller_id: user.id,
        seller_name: currentUserName,
        type: data.type || "image",
        ad_type: data.adType,
        material_url: data.materialUrl || "",
        duration: data.duration || 0,
        is_hidden: false,
        is_expired: false,
        version: 1
      });
    }
    
    setIsAddDialogOpen(false);
  };

  const handleEditAd = (data: any) => {
    if (!selectedAd) return;

    console.log("Updating material URL:", data.materialUrl);

    // Crear el anuncio actualizado manteniendo todos los datos existentes
    // pero actualizando solo el material URL
    const updatedAd: Ad = {
      ...selectedAd,
      materialUrl: data.materialUrl || "",
      version: (selectedAd.version || 0) + 1
    };

    console.log("Updated ad:", updatedAd);
    updateAd(updatedAd);
    setIsEditDialogOpen(false);
  };

  const handleDeleteAd = async () => {
    if (!selectedAd) {
      console.error("No hay anuncio seleccionado para eliminar");
      toast.error("Error: No hay anuncio seleccionado");
      return;
    }
    
    // Verificar que el usuario sea administrador
    if (!isAdmin) {
      console.error("Usuario no tiene permisos de administrador");
      toast.error("Solo los administradores pueden eliminar anuncios");
      return;
    }
    
    setIsDeleting(true);
    try {
      console.log("Iniciando eliminación del anuncio:", selectedAd.id);
      
      // Intentar eliminar el anuncio
      await deleteAd(selectedAd.id);
      
      // Si llegamos aquí, la eliminación fue exitosa
      console.log("Anuncio eliminado exitosamente");
      toast.success("Anuncio eliminado permanentemente");
      
      // Cerrar el diálogo y limpiar el estado
      setIsDeleteDialogOpen(false);
      setSelectedAd(null);
      
      // Forzar una actualización de los datos
      await refreshData();
      
    } catch (error) {
      console.error("Error al eliminar el anuncio:", error);
      
      // Mostrar mensaje de error más descriptivo
      let errorMessage = "Error al eliminar el anuncio";
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Función para actualizar solo el material URL
  const handleUpdateMaterialUrl = async (adId: string, materialUrl: string) => {
    try {
      await updateAdMaterialUrl(adId, materialUrl);
      // Actualizar el estado local del anuncio
      await refreshData();
    } catch (error) {
      console.error("Error al actualizar material URL:", error);
      throw error;
    }
  };

  // Los vendedores solo pueden editar sus propios anuncios, los administradores pueden editar cualquiera
  const canEditOrDeleteAd = (adSellerId: string) => {
    return isAdmin || adSellerId === user?.id;
  };

  return {
    ads,
    isLoading,
    searchName,
    setSearchName,
    searchDate,
    setSearchDate,
    isSearching,
    handleSearch,
    clearSearch,
    isAddDialogOpen,
    setIsAddDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isMaterialDialogOpen,
    setIsMaterialDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedAd,
    isDeleting,
    openAddDialog,
    openEditDialog,
    openMaterialDialog,
    openDeleteDialog,
    handleAddAd,
    handleEditAd,
    handleDeleteAd,
    handleUpdateMaterialUrl,
    canEditOrDeleteAd,
    currentUserName
  };
};
